// src/pages/workflow/PromptTemplateEditPage.tsx
// Create/edit an AI prompt template, with a live test panel that renders
// the prompts against variable values and highlights unresolved placeholders.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Textarea,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { getEntityId } from '@/features/workflow/logic';
import {
  emptyPromptTemplateForm,
  emptyPromptVariable,
  promptTemplateToForm,
  promptVariableTypeOptions,
  validatePromptTemplateForm,
  buildPromptTemplatePayload,
  segmentRenderedPrompt,
  initialTestValues,
  type PromptTemplateFormData,
} from '@/features/workflow/aiConfig';
import type { PromptTemplate } from '@core/types/workflow/PromptTemplate';
import type { TestPromptTemplateResponse } from '@core/types/workflow/TestPromptTemplateResponse';

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
    </svg>
  );
}

function RenderedPrompt({ text, unresolved }: { text: string; unresolved: string[] }) {
  const segments = segmentRenderedPrompt(text, unresolved);
  if (segments.length === 0) return <span className="text-muted-foreground">-</span>;
  return (
    <pre className="whitespace-pre-wrap break-words font-mono text-xs text-foreground">
      {segments.map((seg, i) =>
        seg.unresolved ? (
          <mark key={i} className="bg-destructive/20 text-destructive rounded px-0.5">{seg.text}</mark>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </pre>
  );
}

export const PromptTemplateEditPage = observer(() => {
  const store = useWorkflow();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams<{ templateId: string }>();
  const isCreate = !templateId;
  const cloneSource = (location.state as { clone?: PromptTemplate } | null)?.clone;

  const [formData, setFormData] = useState<PromptTemplateFormData>(() => {
    if (isCreate && cloneSource) {
      const form = promptTemplateToForm(cloneSource);
      form.slug = '';
      return form;
    }
    return emptyPromptTemplateForm();
  });
  const [isBuiltin, setIsBuiltin] = useState(false);
  const [isLoading, setIsLoading] = useState(!isCreate);
  const [loadFailed, setLoadFailed] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Test panel state
  const [testValues, setTestValues] = useState<Record<string, string>>(() =>
    initialTestValues(isCreate && cloneSource ? cloneSource.variables || [] : []),
  );
  const [testResult, setTestResult] = useState<TestPromptTemplateResponse | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    setIsLoading(true);
    store
      .GetPromptTemplate(templateId)
      .then((res) => {
        if (cancelled) return;
        const tpl = res?.prompt_template;
        if (tpl) {
          setFormData(promptTemplateToForm(tpl));
          setIsBuiltin(!!tpl.is_builtin);
          setTestValues(initialTestValues(tpl.variables || []));
        } else {
          setLoadFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const readOnly = isBuiltin;

  const setField = <K extends keyof PromptTemplateFormData>(key: K, value: PromptTemplateFormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (formErrors[key as string]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    }
  };

  const setVariable = (index: number, patch: Partial<PromptTemplateFormData['variables'][number]>) => {
    setFormData((prev) => ({
      ...prev,
      variables: prev.variables.map((v, i) => (i === index ? { ...v, ...patch } : v)),
    }));
  };

  const addVariable = () => {
    setFormData((prev) => ({ ...prev, variables: [...prev.variables, emptyPromptVariable()] }));
  };

  const removeVariable = (index: number) => {
    setFormData((prev) => ({ ...prev, variables: prev.variables.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    const errors = validatePromptTemplateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = buildPromptTemplatePayload(formData);
      if (!templateId) {
        const res = await store.CreatePromptTemplate({ prompt_template: payload });
        const newId = getEntityId(res?.prompt_template);
        if (newId) {
          navigate(`/workflows/prompt-templates/${newId}`, { replace: true });
        } else {
          navigate('/workflows/prompt-templates');
        }
      } else {
        await store.UpdatePromptTemplate(templateId, { prompt_template: payload });
        navigate('/workflows/prompt-templates');
      }
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!templateId || readOnly) return;
    if (!confirm(t('workflow.deletePromptTemplateConfirm'))) return;
    try {
      await store.DeletePromptTemplate(templateId);
      navigate('/workflows/prompt-templates');
    } catch {
      // Error handled by store
    }
  };

  const handleTest = async () => {
    if (!templateId) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const res = await store.TestPromptTemplate(templateId, { variables: testValues });
      setTestResult(res);
    } catch {
      // Error handled by store
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-muted rounded w-1/3" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="space-y-4">
        {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-muted-foreground mb-4">{t('workflow.promptTemplateNotFound')}</p>
            <Link to="/workflows/prompt-templates">
              <Button variant="secondary">{t('common.back')}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link to="/workflows/prompt-templates" className="text-muted-foreground hover:text-foreground">{t('workflow.promptTemplates')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{isCreate ? t('workflow.addPromptTemplate') : formData.name || formData.slug}</span>
          </nav>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-foreground">
              {isCreate ? t('workflow.addPromptTemplate') : formData.name}
            </h1>
            {isBuiltin && <Badge variant="secondary" size="sm">{t('workflow.builtin')}</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isCreate && (
            <span title={readOnly ? t('workflow.builtinReadOnly') : undefined}>
              <Button variant="ghost" size="sm" className="text-destructive" disabled={readOnly} onClick={handleDelete}>
                {t('common.delete')}
              </Button>
            </span>
          )}
          <Button variant="secondary" size="sm" onClick={() => navigate('/workflows/prompt-templates')}>
            {t('common.cancel')}
          </Button>
          <span title={readOnly ? t('workflow.builtinReadOnly') : undefined}>
            <Button size="sm" disabled={readOnly || isSubmitting} isLoading={isSubmitting} onClick={handleSave}>
              {t('common.save')}
            </Button>
          </span>
        </div>
      </div>

      {isBuiltin && (
        <div className="rounded-lg border border-border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
          {t('workflow.builtinPromptTemplateHint')}
        </div>
      )}

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        <div className="xl:col-span-2 space-y-6">
          {/* General */}
          <Card>
            <CardHeader>
              <CardTitle>{t('workflow.general')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label={t('workflow.field.name')}
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  error={formErrors.name ? t(formErrors.name) : undefined}
                  disabled={readOnly}
                  required
                />
                <Input
                  label={t('workflow.field.slug')}
                  value={formData.slug}
                  onChange={(e) => setField('slug', e.target.value)}
                  error={formErrors.slug ? t(formErrors.slug) : undefined}
                  helperText={isCreate ? t('workflow.slugHint') : t('workflow.slugImmutable')}
                  disabled={readOnly || !isCreate}
                  className="font-mono"
                  required
                />
              </div>
              <Input
                label={t('workflow.field.description')}
                value={formData.description}
                onChange={(e) => setField('description', e.target.value)}
                disabled={readOnly}
              />
              <ToggleSwitch
                label={t('workflow.field.isActive')}
                checked={formData.is_active}
                onChange={(checked) => setField('is_active', checked)}
                disabled={readOnly}
              />
            </CardContent>
          </Card>

          {/* Prompts */}
          <Card>
            <CardHeader>
              <CardTitle>{t('workflow.prompts')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                label={t('workflow.field.systemPrompt')}
                value={formData.system_prompt}
                onChange={(e) => setField('system_prompt', e.target.value)}
                rows={10}
                className="font-mono"
                spellCheck={false}
                disabled={readOnly}
                helperText={t('workflow.promptPlaceholderHint')}
              />
              <Textarea
                label={t('workflow.field.userPrompt')}
                value={formData.user_prompt}
                onChange={(e) => setField('user_prompt', e.target.value)}
                rows={10}
                className="font-mono"
                spellCheck={false}
                disabled={readOnly}
              />
            </CardContent>
          </Card>

          {/* Variables */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('workflow.variables')}</CardTitle>
                {!readOnly && (
                  <Button variant="secondary" size="sm" onClick={addVariable}>
                    {t('workflow.addVariable')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {formData.variables.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('workflow.noVariables')}</p>
              ) : (
                formData.variables.map((v, i) => (
                  <div key={i} className="rounded-lg border border-border p-3 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        label={t('workflow.varField.name')}
                        value={v.name}
                        onChange={(e) => setVariable(i, { name: e.target.value })}
                        error={formErrors[`variable_${i}`] ? t(formErrors[`variable_${i}`]) : undefined}
                        className="font-mono"
                        disabled={readOnly}
                        required
                      />
                      <Input
                        label={t('workflow.varField.label')}
                        value={v.label}
                        onChange={(e) => setVariable(i, { label: e.target.value })}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                      <Select
                        label={t('workflow.varField.type')}
                        value={v.type || 'string'}
                        onChange={(e) => setVariable(i, { type: e.target.value })}
                        disabled={readOnly}
                      >
                        {promptVariableTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                        ))}
                      </Select>
                      <Input
                        label={t('workflow.varField.default')}
                        value={v.default_value}
                        onChange={(e) => setVariable(i, { default_value: e.target.value })}
                        disabled={readOnly}
                      />
                      <div className="flex items-center justify-between gap-2 pb-2">
                        <label className="flex items-center gap-2 text-sm text-foreground">
                          <input
                            type="checkbox"
                            className="rounded border-border"
                            checked={v.required}
                            onChange={(e) => setVariable(i, { required: e.target.checked })}
                            disabled={readOnly}
                          />
                          {t('workflow.varField.required')}
                        </label>
                        {!readOnly && (
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeVariable(i)}>
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Model settings */}
          <Card>
            <CardHeader>
              <CardTitle>{t('workflow.modelSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Input
                  label={t('workflow.field.model')}
                  value={formData.model}
                  onChange={(e) => setField('model', e.target.value)}
                  error={formErrors.model ? t(formErrors.model) : undefined}
                  className="font-mono"
                  disabled={readOnly}
                  required
                />
                <Input
                  label={t('workflow.field.temperature')}
                  value={formData.temperature}
                  onChange={(e) => setField('temperature', e.target.value)}
                  error={formErrors.temperature ? t(formErrors.temperature) : undefined}
                  inputMode="decimal"
                  disabled={readOnly}
                />
                <Input
                  label={t('workflow.field.maxTokens')}
                  value={formData.max_tokens}
                  onChange={(e) => setField('max_tokens', e.target.value)}
                  error={formErrors.max_tokens ? t(formErrors.max_tokens) : undefined}
                  inputMode="numeric"
                  disabled={readOnly}
                />
              </div>
              <Textarea
                label={t('workflow.field.responseSchema')}
                value={formData.response_schema}
                onChange={(e) => setField('response_schema', e.target.value)}
                error={formErrors.response_schema ? t(formErrors.response_schema) : undefined}
                rows={6}
                className="font-mono"
                spellCheck={false}
                disabled={readOnly}
                helperText={t('workflow.responseSchemaHint')}
              />
            </CardContent>
          </Card>
        </div>

        {/* Test panel */}
        <Card className="xl:sticky xl:top-4">
          <CardHeader>
            <CardTitle>{t('workflow.testTemplate')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isCreate ? (
              <p className="text-sm text-muted-foreground">{t('workflow.testRequiresSave')}</p>
            ) : (
              <>
                {formData.variables.filter((v) => v.name).length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('workflow.noVariables')}</p>
                ) : (
                  formData.variables
                    .filter((v) => v.name)
                    .map((v) => (
                      <Input
                        key={v.name}
                        label={v.label || v.name}
                        helperText={v.description || undefined}
                        value={testValues[v.name] || ''}
                        onChange={(e) =>
                          setTestValues((prev) => ({ ...prev, [v.name]: e.target.value }))
                        }
                        required={v.required}
                      />
                    ))
                )}
                <Button fullWidth disabled={isTesting} isLoading={isTesting} onClick={handleTest}>
                  {t('workflow.runTest')}
                </Button>

                {testResult && (
                  <div className="space-y-3">
                    {testResult.unresolved?.length > 0 && (
                      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {t('workflow.unresolvedVariables')}{' '}
                        <span className="font-mono">{testResult.unresolved.join(', ')}</span>
                      </div>
                    )}
                    <div>
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">{t('workflow.renderedSystem')}</h4>
                      <div className="bg-muted rounded-md p-2 max-h-56 overflow-y-auto">
                        <RenderedPrompt text={testResult.system} unresolved={testResult.unresolved || []} />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">{t('workflow.renderedUser')}</h4>
                      <div className="bg-muted rounded-md p-2 max-h-56 overflow-y-auto">
                        <RenderedPrompt text={testResult.user} unresolved={testResult.unresolved || []} />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
});

export default PromptTemplateEditPage;
